import { ReduxState, crudGetList, ListControllerProps } from "ra-core";
import { useRef, useCallback, ChangeEvent, useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RecPatch } from "~/dataProvider/tools";
import { EncryptionRuleDataTypeMappingResponseDto, DataCatalogSelectAllItemResponseDto, BulkUpdateSelectedInput, BulkUpdateValidationFailedIdsResponseDto } from "~/gen/api/governance";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { DataTypeMaskingKind, decideDataTypeMaskingKind } from "../fieldEdit/maskingRuleDataTypesMap";
import { FieldSelecAll, DataCatalogInternalMutableState, BulkEditInfo } from "./types";


export interface BulkEditModifiers {
  handleSelectAll: (event:ChangeEvent<HTMLInputElement>) => void,
  bulkEditEncryptRules: EncryptionRuleDataTypeMappingResponseDto[],
  bulkEditMaskingKind: { kind: DataTypeMaskingKind, datatype?: string },
  onToggleItem: (fieldRecId:string, event:ChangeEvent<HTMLInputElement>) => void,
  cancelBulkEdit: () => void,
  startBulkEdit: () => void,
  retrieveAllSelectedIds: () => BulkUpdateSelectedInput[],
  removeManyFromDraft: (ids: BulkUpdateValidationFailedIdsResponseDto[]) => void,
}

export const useBulkEdit = (innerState:DataCatalogInternalMutableState, listCtrl: ListControllerProps): BulkEditModifiers => {
  const [recalculate, setRecalculate] = useState(0)
  const selectedIds = useSelector<ReduxState, FieldSelecAll&RecPatch | undefined>(store => store.admin.resources['govrnc-select-all-fields']?.data['1'] as any)
  const { selectedIds: listSelectedIds, onSelect, onToggleItem: listOnToggleItem, data, total } = listCtrl
  innerState.pageData = (data || {}) as any

  const dispatch = useDispatch()

  const prevSelectAllReqSignature = useRef('')
  const handleSelectAll = useCallback((event:ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (prevSelectAllReqSignature.current !== innerState.bulkEdit.signature) {
        dispatch(crudGetList('govrnc-select-all-fields', pages(), sorts(), innerState.filtersObj))
        prevSelectAllReqSignature.current = innerState.bulkEdit.signature  
      } else if (selectedIds?.ids) {
        writeManyToDraft(selectedIds.ids)
      }
    } else {
      cancelBulkEdit()
    }
  }, [dispatch, selectedIds])
  
  const bulkEditEncryptRules = useMemo(() => {
    if (Object.keys(innerState.bulkEdit.draft.notEncryptable).length > 0) { return [] }

    return Object.values(
      Object.keys(innerState.bulkEdit.draft.encryptable).map(l1Key => innerState.encryptRuleMap[l1Key] || [])
      .reduce((prevDict, ls, i) => { // effectively calculate intersections of all rule sets
        const dict = {} as Record<number, EncryptionRuleDataTypeMappingResponseDto>
        for (const x of ls) {
          if (i === 0 || prevDict[x.id as number]) {
            dict[x.id as number] = x
          }
        }
        return dict
      }, {} as Record<number, EncryptionRuleDataTypeMappingResponseDto>)
    ).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1)
  }, [recalculate])

  const bulkEditMaskingKind: {kind:DataTypeMaskingKind,datatype?:string} = useMemo(() => {
    let prevKind: DataTypeMaskingKind|null = null
    const l1Keys = 
      Object.keys(innerState.bulkEdit.draft.encryptable)
        .concat(Object.keys(innerState.bulkEdit.draft.notEncryptable))

    return l1Keys.length !== 1
      ? { kind: 'not_support' }
      : (((datatype) => ({ kind: decideDataTypeMaskingKind(datatype), datatype }))(l1Keys[0].split('-')[1]));
  }, [recalculate])
    
  innerState.bulkEdit.count = useMemo(() => {
    return Object.values(innerState.bulkEdit.draft.encryptable)
      .concat(Object.values(innerState.bulkEdit.draft.notEncryptable))
      .map(bag => Object.keys(bag).length).reduce((acc, n) => acc + n, 0)
  }, [recalculate, innerState.bulkEdit.signature])
  innerState.bulkEdit.fullChecked = 
    innerState.bulkEdit.count > 0 &&
    innerState.bulkEdit.count === (selectedIds?.ids.length || total)
  
  const retrieveAllSelectedIds = useCallback(() => {
    return Object.values(innerState.bulkEdit.draft.encryptable)
      .concat(Object.values(innerState.bulkEdit.draft.notEncryptable))
      .flatMap(bag => Object.keys(bag)).map(idStr => {
        const arr = idStr.split('-')
        return {
          databaseId: Number.parseInt(arr[0]),
          tableId: Number.parseInt(arr[2]),
          fieldId: Number.parseInt(arr[3]),
        }
      })
  }, [])

  const hasEncryptRules = (l1Key: string) => {
    return !!innerState.encryptRuleMap[l1Key]
  }

  const writeDraft = (x: DataCatalogSelectAllItemResponseDto, isDelete: boolean, skipRecalc?: boolean) => {
    const databaseType = x.databaseType
    const l1Key = `${databaseType}-${x.type}`
    const l2Key = `${x.databaseId}-${x.schemaName}-${x.tableId}-${x.fieldId}`
    const book = hasEncryptRules(l1Key) ? innerState.bulkEdit.draft.encryptable : innerState.bulkEdit.draft.notEncryptable

    if (!isDelete) {
      const bag = book[l1Key] || (book[l1Key] = {})
      bag[l2Key] = true
      !skipRecalc && setRecalculate(n => n + 1)
    } else {
      const bag = book[l1Key]
      if (bag && bag[l2Key]) {
        delete bag[l2Key]
        if (Object.keys(bag).length === 0) {
          delete book[l1Key]
        }
        !skipRecalc && setRecalculate(n => n + 1)
      }
    }
  }

  const writeManyToDraft = (ids: DataCatalogSelectAllItemResponseDto[]) => {
    onSelect(ids.map(x => `${x.databaseId}-${x.schemaName}-${x.tableId}-${x.fieldId}`))
    for(var x of ids) {
      writeDraft(x, false, true)
    }
    setRecalculate(n => n + 1)
  }

  useEffect(() => {
    if (selectedIds?.ids) {
      writeManyToDraft(selectedIds.ids)      
    }
  }, [selectedIds?.ids])

  
  const removeManyFromDraft = useCallback((ids: BulkUpdateValidationFailedIdsResponseDto[]) => {
    const encryptableL1Keys = Object.keys(innerState.bulkEdit.draft.encryptable)
    const notEncryptableL1Keys = Object.keys(innerState.bulkEdit.draft.notEncryptable)
    
    const removedFieldIds = ids.flatMap(raw => {
      const id = raw as Required<BulkUpdateValidationFailedIdsResponseDto>
      const database = innerState.databaseMap[id.databaseId]
      const databaseType = database.databaseType
      const tests = [`${databaseType}-`, `${id.databaseId}-`, `-${id.tableId}-${id.fieldId}`]
      
      const tryDelete = (l1Key:string, book:BulkEditInfo['draft']['encryptable']) => {
        if (l1Key) {
          const bag = book[l1Key] || {}
          const l2Key = Object.keys(bag).find(l2Key => l2Key.startsWith(tests[1]) && l2Key.endsWith(tests[2]))
          if (l2Key) {
            delete bag[l2Key]
            if (Object.keys(bag).length === 0) {
              delete book[l1Key]
            }
            return l2Key
          }
        }
      }

      let l1Keys_e = encryptableL1Keys.filter(l1Key => l1Key.startsWith(tests[0]))
      let l2Key = l1Keys_e.reduce((acc:string|undefined, l1Key) => {
        if (acc) return acc
        return tryDelete(l1Key, innerState.bulkEdit.draft.encryptable)
      }, undefined)
      
      if (!l2Key) {
        const l1Keys_n = notEncryptableL1Keys.filter(l1Key => l1Key.startsWith(tests[0]))
        l2Key = l1Keys_n.reduce((acc:string|undefined, l1Key) => {
          if (acc) return acc
          return tryDelete(l1Key, innerState.bulkEdit.draft.notEncryptable)
        }, undefined)
      }
      return l2Key ? [l2Key] : []
    })
    
    if (Object.keys(innerState.bulkEdit.draft.encryptable).length === 0
    && Object.keys(innerState.bulkEdit.draft.notEncryptable).length === 0) {
      cancelBulkEdit()
    } else {
      const rSet = new Set(removedFieldIds)
      onSelect(listSelectedIds.filter(x => !rSet.has(x as string)))
      setRecalculate(n => n + 1)
      dispatch({
        type: 'OD/CLEAR_GOVRNC_VALI_BULK',
        payload: {data:{ id: 1, failed:[] }},
        meta: {
          resource: 'govrnc-vali-bulk',
          fetchResponse: 'GET_ONE',
          fetchStatus: 'RA/FETCH_END',
        },
      })
    }
  }, [listSelectedIds])

  const onToggleItem = useCallback((fieldRecId:string, event:ChangeEvent<HTMLInputElement>) => {
    const field = innerState.pageData[fieldRecId]
    if (!field) { return }
    const databaseType = innerState.databaseMap[field.databaseId || 0].databaseType

    writeDraft({ 
      databaseId: field.databaseId, 
      databaseType,
      type: field.type,
      schemaName: field.schemaName,
      tableId: field.tableId,
      fieldId: field.fieldId,
    }, !event.target.checked)
    listOnToggleItem(fieldRecId)
  }, [listOnToggleItem])
  
  const cancelBulkEdit = useCallback(() => {
    prevSelectAllReqSignature.current = ''
    innerState.lockdown = false
    innerState.bulkEdit.draft = { encryptable:{}, notEncryptable:{} }
    innerState.bulkEdit.open = false
    setRecalculate(n => n + 1)
    dispatch({
      type: 'OD/CLEAR_GOVRNC_SELECT_FIELDS',
      payload: {data:{ id: 1, ids:[] }},
      meta: {
        resource: 'govrnc-select-all-fields',
        fetchResponse: 'GET_ONE',
        fetchStatus: 'RA/FETCH_END',
      },
    })
    dispatch({
      type: 'OD/CLEAR_GOVRNC_VALI_BULK',
      payload: {data:{ id: 1, failed:[] }},
      meta: {
        resource: 'govrnc-vali-bulk',
        fetchResponse: 'GET_ONE',
        fetchStatus: 'RA/FETCH_END',
      },
    })
    onSelect([])
  }, [])
  
  const startBulkEdit = useCallback(() => {
    if (!innerState.lockdown && innerState.bulkEdit.count > 0) {
      innerState.lockdown = true
      innerState.bulkEdit.open = true
      setRecalculate(n => n + 1)
    }
  }, [])

  useEffect(() => {
    return () => {
      innerState.bulkEdit.signature = ''
      cancelBulkEdit()
    }
  }, [])

  return {
    handleSelectAll,
    bulkEditEncryptRules,
    bulkEditMaskingKind,
    onToggleItem,
    cancelBulkEdit,
    startBulkEdit,
    retrieveAllSelectedIds,
    removeManyFromDraft,
  }
}
